import { useState, useEffect, useCallback } from 'react';

export interface TourStep {
    id: string;
    target: string;
    title: string;
    content: string;
    placement?: 'top' | 'bottom' | 'left' | 'right';
}

export interface UseTourReturn {
    currentStep: number;
    isActive: boolean;
    currentTourStep: TourStep | null;
    nextStep: () => void;
    previousStep: () => void;
    skipTour: () => void;
    resetTour: () => void;
    startTour: () => void;
}

export function useTour(steps: TourStep[]): UseTourReturn {
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [isActive, setIsActive] = useState<boolean>(false);
    const tourStartDelay = 2000;

    useEffect(() => {
        if (steps.length > 0) {
            setTimeout(() => {
                setIsActive(true);
            }, tourStartDelay);
        }
    }, [steps.length]);

    const currentTourStep = isActive && currentStep < steps.length ? steps[currentStep] : null;

    const nextStep = useCallback(() => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            setIsActive(false);
            setCurrentStep(0);
        }
    }, [currentStep, steps.length]);

    const previousStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    }, [currentStep]);

    const skipTour = useCallback(() => {
        setIsActive(false);
        setCurrentStep(0);
    }, []);

    const resetTour = useCallback(() => {
        setCurrentStep(0);
        setIsActive(true);
    }, []);

    const startTour = useCallback(() => {
        setCurrentStep(0);
        setIsActive(true);
    }, []);

    return {
        currentStep,
        isActive,
        currentTourStep,
        nextStep,
        previousStep,
        skipTour,
        resetTour,
        startTour,
    };
}
